import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';

import UPGRADE_IMAGE from 'assets/images/upgrade-image.webp';
import UPGRADE_IMAGE_CERTIFICATE from 'assets/images/upgrade-image-certificate.webp';
import UPGRADE_IMAGE_SKILL from 'assets/images/upgrade-image-skill.webp';
import UPGRADE_IMAGE_VIDEO from 'assets/images/upgrade-image-video.webp';
import UPGRADE_IMAGE_SCORM from 'assets/images/upgrade-image-scorm.webp';
import LI_DOT from 'assets/images/li-dot.webp';
import { IFeature } from 'graphql/types';
import { Img } from 'common/components/Img';
import { PageTitle } from 'common/components/PageTitle';
import { PlanDemo } from 'common/components/PlanDemo';

interface IFeatureConfig {
    translationPrefix: string;
    image: string;
}

const FEATURE_CONFIG: Partial<Record<IFeature, IFeatureConfig>> = {
    [IFeature.Certificate]: {
        translationPrefix: 'plans.certificate',
        image: UPGRADE_IMAGE_CERTIFICATE,
    },
    [IFeature.Dashboard]: {
        translationPrefix: 'plans.dashboard',
        image: UPGRADE_IMAGE,
    },
    [IFeature.Library]: {
        translationPrefix: 'plans.library',
        image: UPGRADE_IMAGE,
    },
    [IFeature.Offer]: {
        translationPrefix: 'plans.offers',
        image: UPGRADE_IMAGE,
    },
    [IFeature.Portfolio]: {
        translationPrefix: 'plans.portfolio',
        image: UPGRADE_IMAGE,
    },
    [IFeature.Skill]: {
        translationPrefix: 'plans.skill',
        image: UPGRADE_IMAGE_SKILL,
    },
    [IFeature.Video]: {
        translationPrefix: 'plans.video',
        image: UPGRADE_IMAGE_VIDEO,
    },
    [IFeature.Scorm]: {
        translationPrefix: 'plans.scorm',
        image: UPGRADE_IMAGE_SCORM,
    },
};

interface IFeatureUpgradeContentProps {
    feature: IFeature;
}

export const FeatureUpgradeContent = ({
    feature,
}: IFeatureUpgradeContentProps) => {
    const [translate] = useTranslation();

    const config = FEATURE_CONFIG[feature];
    const prefix =
        config?.translationPrefix ?? `plans.${feature.toLowerCase()}`;
    const image = config?.image ?? UPGRADE_IMAGE;

    const title = translate(`${prefix}.title`, {
        defaultValue: translate('featureUpgradeContent.defaultTitle'),
    });
    const description = translate(`${prefix}.text`, {
        defaultValue: translate('featureUpgradeContent.defaultDescription'),
    });
    const moduleTitle = translate(`${prefix}.moduleTitle`, {
        defaultValue: '',
    });
    const advantagesText = translate(`${prefix}.advantagesText`, {
        defaultValue: '',
    });

    const advantages = [1, 2, 3, 4]
        .map((i) =>
            translate(`${prefix}.advantages.advantage${i}`, {
                defaultValue: '',
            })
        )
        .filter(Boolean);

    return (
        <Box sx={{ mt: 8, mb: 4 }}>
            <PageTitle>{title}</PageTitle>
            <Grid
                container
                sx={{
                    mt: 0.5,
                    flexWrap: 'wrap',
                    alignItems: 'center',
                }}
            >
                <Grid size={{ md: 6, sm: 12 }} sx={{ mb: 2, pr: { md: 4 } }}>
                    {moduleTitle && (
                        <Typography
                            sx={{
                                color: 'primary.main',
                                fontWeight: 700,
                                textTransform: 'uppercase',
                                fontSize: 14,
                            }}
                        >
                            {moduleTitle}
                        </Typography>
                    )}

                    <Typography sx={{ mb: 2 }} variant="h1">
                        {title}
                    </Typography>

                    <Typography lineHeight="1.6" sx={{ mb: 2 }}>
                        {description}
                    </Typography>

                    {advantagesText && (
                        <Typography fontWeight={700}>
                            {advantagesText}
                        </Typography>
                    )}

                    {advantages.length > 0 && (
                        <Box sx={{ mt: 1 }}>
                            {advantages.map((advantage) => (
                                <Box
                                    key={advantage}
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        mb: 1,
                                    }}
                                >
                                    <Box
                                        sx={{
                                            backgroundImage: `url(${LI_DOT})`,
                                            backgroundSize: '20px',
                                            backgroundRepeat: 'no-repeat',
                                            height: 20,
                                            width: 20,
                                            mr: 2,
                                            flexShrink: 0,
                                        }}
                                    />
                                    <Typography lineHeight="1.6">
                                        {advantage}
                                    </Typography>
                                </Box>
                            ))}
                        </Box>
                    )}

                    <PlanDemo sx={{ mt: 2 }} />
                </Grid>

                <Grid size={{ md: 6, sm: 12 }}>
                    <Img src={image} />
                </Grid>
            </Grid>
        </Box>
    );
};
